import { MediaMatcher } from '@angular/cdk/layout';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivationStart, Router, RouterModule } from '@angular/router';
import { Subject, combineLatest, map, of, switchMap, take, takeUntil } from 'rxjs';
import { SanitizerPipe } from 'src/app/shared/pipes';
import { CustomerService, SessionService } from 'src/app/shared/services';
import { SidenavService } from '../services/side-nav.service';

@Component({
    standalone: true,
    selector: 'app-header-login',
    templateUrl: './header-login.component.html',
    styleUrls: ['./header-login.component.scss'],
    imports: [
        SanitizerPipe,
        RouterModule,
        MatIconModule,
        MatMenuModule,
        MatButtonModule,
        MatListModule,
        MatTooltipModule,
    ],
})
export class HeaderLoginComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    public userName: string;
    public isAuthenticated = false;
    public showLogin = true;
    public lastPath = '';
    public smallQuery: MediaQueryList;

    @Input() isMobile = false;
    @Input() loginDisallowedRoutes: string[] = [];
    @Input() administrativeLoginURL: string;
    @Input() enableMenu = false;

    constructor(
        private router: Router,
        private sessionService: SessionService,
        private customerService: CustomerService,
        private sidenavService: SidenavService,
        private media: MediaMatcher
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.smallQuery = this.media.matchMedia('(max-width: 900px)');
    }

    ngOnInit(): void {
        this.subscribeAvailablesRouteToShowLogin();
        this.subscribeIsUserAuthenticated();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribeIsUserAuthenticated(): void {
        this.sessionService
            .isAuthenticated$()
            .pipe(
                switchMap((isAuthenticated) => {
                    if (!isAuthenticated) {
                        return of({ userName: '', isAuthenticated: false });
                    }

                    return this.customerService.getUserName().pipe(
                        take(1),
                        map((userName) => ({
                            userName,
                            isAuthenticated,
                        }))
                    );
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(({ userName, isAuthenticated }) => {
                this.userName = userName || '';
                this.isAuthenticated = isAuthenticated;
            });
    }

    logOut(): void {
        this.sessionService.logout().pipe(take(1)).subscribe();
    }

    toggleRightSidenav(): void {
        this.sidenavService.toggle();
    }

    subscribeAvailablesRouteToShowLogin(): void {
        combineLatest([this.router.events, this.sessionService.isAuthenticated$()])
            .pipe(
                map(([routerEvent]) => {
                    if (routerEvent instanceof ActivationStart) {
                        this.lastPath = routerEvent.snapshot.routeConfig?.path || '';
                    }

                    return { path: this.lastPath };
                }),
                map(({ path }) => {
                    return this.displayLogin(path);
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((isAvailable: boolean) => {
                this.showLogin = isAvailable;
            });
    }

    displayLogin(url: string): boolean {
        return !this.loginDisallowedRoutes.find((element) => element === url);
    }
}
